import './index.less';

import {
  Box,
  Flex,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  useColorMode,
  useColorStyle,
  Link as TLink,
} from '@tonic-one/react';
import { ErrorIcon } from '@tonic-one/react-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import DataTrack from '../../../components/DataTrack';
import TableTemplate from '../Charts/TableTemplate';
import { Report } from './report';
import { MailServices } from '@Utils/const';
import { handleLinkClick } from '@Utils/common';
import SmartTooltip from '@Report/SmartTooltip';
import { Service } from '@Types/app';

const ReportTable = (props: {
  report: Report | null;
  services: string[];
  threat: string;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();

  const serviceData: any[] = [];
  const detailData: any[] = [];

  for (const service of props.services) {
    (props.report?.[service]?.[props.threat] || [])
      .filter((x) => !x.legal)
      .sort((a, b) => {
        // sort by protection and ruleName
        if (a.protection !== b.protection) {
          return -(a.protection - b.protection);
        }
        const locA = t(`recommendConfig.rule.${a.ruleName}`);
        const locB = t(`recommendConfig.rule.${b.ruleName}`);
        if (locA < locB) {
          return -1;
        } else if (locA > locB) {
          return 1;
        } else {
          return 0;
        }
      })
      .forEach((x) => {
        serviceData.push({
          service,
          hideText: false,
          hideBorder: false,
        });
        detailData.push([
          <Box display="inline">
            <Icon
              position="relative"
              top="-1px"
              as={ErrorIcon}
              color="#E52630"
              mr="12px"
            />
            {t(`recommendConfig.rule.${x.ruleName}`)}
          </Box>,
          mapProtectionLevel(x.protection, colorMode, colorStyle),
          renderLocation(
            t,
            x.filter,
            props.threat,
            service,
            x.ruleName,
            x.protection
          ),
        ]);
      });
  }

  for (let idx = 1; idx < serviceData.length; idx += 1) {
    if (serviceData[idx - 1].service === serviceData[idx].service) {
      serviceData[idx - 1].hideBorder = true;
      serviceData[idx].hideText = true;
    }
  }

  return (
    <Flex
      direction="row"
      align="flex-start"
      justify="flex-start"
      p="12px 16px 0"
    >
      <Table width="15%" fontSize="14px" overflow="hidden">
        <TableHeader
          background={
            colorMode === 'dark' ? '' : colorStyle.background.secondary
          }
        >
          <TableHeaderRow height="36px" width="100%">
            <TableHeaderCell
              width="100%"
              textAlign="left"
              color={colorStyle.color.secondary}
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {t('recommendConfig.filter.services')}
            </TableHeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {serviceData.map((item, i: number) => (
            <TableRow key={i} height="36px" width="100%">
              <TableCell
                width="100%"
                textAlign="left"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                borderColor={item.hideBorder ? 'transparent' : undefined}
                color={item.hideText ? 'transparent' : colorStyle.color.primary}
              >
                <SmartTooltip>
                  {t(`common.service.${item.service}`)}
                </SmartTooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TableTemplate
        width="85%"
        config={[
          { width: '35%', align: 'left' },
          { width: '12%', align: 'left' },
          { width: '53%', align: 'left' },
        ]}
        header={[
          t('recommendConfig.filter.featuresNotAdopted'),
          t('recommendConfig.filter.importance'),
          t('recommendConfig.filter.configurationPath'),
        ]}
        data={detailData}
        isRecommendConfig
      />
    </Flex>
  );
};

function renderLocation(
  t: TFunction<'translation'>,
  filter: string,
  threat: string,
  service: string,
  ruleName: string,
  protection: number
) {
  const AllMailServices = [
    ...MailServices,
    'gmail_inline_inbound',
    'gmail_inline_outbound',
    'exchange_inline_inbound',
    'exchange_inline_outbound',
  ];
  const collectionType = AllMailServices.includes(service as Service)
    ? 'email'
    : 'collaboration';
  if (filter === 'GlobalSettings') {
    return (
      <Box display="inline">
        <DataTrack
          type="ConfigRecommendation.FeatureAdoption.Location.Click"
          metadata={{ threat, service, ruleName }}
        >
          <TLink
            onClick={handleLinkClick}
            href={`policy/global-settings/high-profile-list`}
            target="_blank"
          >
            {t('recommendConfig.rule.setting')}
          </TLink>
        </DataTrack>
        {!filter ? '' : ' > ' + t(`apt.filter.${filter}`)}
      </Box>
    );
  } else {
    return (
      <Box display="inline">
        <DataTrack
          type="ConfigRecommendation.FeatureAdoption.Location.Click"
          metadata={{ threat, service, ruleName, protection }}
        >
          <TLink
            onClick={handleLinkClick}
            href={`policy/advanced-threat-protection/${collectionType}`}
            target="_blank"
          >
            {t('recommendConfig.rule.atp')}
          </TLink>
        </DataTrack>
        {!filter
          ? ` > ${t('recommendConfig.filter.yourPolicy')}`
          : ` > ${t('recommendConfig.filter.yourPolicy')} > ${t(
              `apt.filter.${filter}`
            )}`}
      </Box>
    );
  }
}

function mapProtectionLevel(level: number, colorMode: string, colorStyle: any) {
  const fillColor = colorStyle.color.emphasis;
  const emptyColor = colorMode === 'dark' ? 'gray:70' : 'gray:30';
  const rounds = [];
  for (let idx = 10; idx <= 30; idx += 10) {
    rounds.push(
      <Box
        key={idx}
        width="8px"
        height="8px"
        borderRadius="4px"
        background={idx <= level ? fillColor : emptyColor}
      />
    );
  }
  return (
    <Flex
      width="40px"
      height="23px"
      direction="row"
      align="center"
      justify="space-between"
    >
      {rounds}
    </Flex>
  );
}

export default ReportTable;
